import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import type { ApiFetch } from "../types/api";
import { deleteInternalUser, listInternalUsers, saveInternalUser, searchEntraUsers } from "../features/internal-access/api";
import type { EntraUser, InternalUserAccess, InternalUserAccessInput } from "../features/internal-access/types";

import { assignablePortalPages, canAssignPortalPage } from "../config/internalPortalPages";

type EditorLocation = "search" | "existing" | null;

function pageLabels(pageKeys: string[]) {
  return pageKeys.map((key) => assignablePortalPages.find((page) => page.key === key)?.permissionLabel || key).join(", ");
}

function sameUserObjectId(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export default function InternalUserManagementPage({ apiFetch }: { apiFetch: ApiFetch }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<EntraUser[]>([]);
  const [selected, setSelected] = useState<EntraUser | null>(null);
  const [editorLocation, setEditorLocation] = useState<EditorLocation>(null);
  const [form, setForm] = useState<InternalUserAccessInput>({ email: "", displayName: "", role: "user", portalAccess: true, pageKeys: [] });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [existingUsers, setExistingUsers] = useState<InternalUserAccess[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const existingUserElements = useRef<Record<string, HTMLDivElement | null>>({});

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setExistingUsers(await listInternalUsers(apiFetch));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Bestaande toegang kon niet worden geladen.");
    } finally {
      setLoadingUsers(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    if (editorLocation !== "existing" || !selected) return;
    existingUserElements.current[selected.id]?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [editorLocation, selected]);

  const choose = (user: EntraUser) => {
    const storedAccess = existingUsers.find((existingUser) => sameUserObjectId(existingUser.userObjectId, user.id));
    setSelected(storedAccess ? { ...user, id: storedAccess.userObjectId } : user);
    setEditorLocation(storedAccess ? "existing" : "search");
    setMatches([]);
    setSearched(false);
    setQuery("");
    setForm(storedAccess ? {
      email: storedAccess.email,
      displayName: storedAccess.displayName,
      role: storedAccess.role,
      portalAccess: storedAccess.portalAccess,
      pageKeys: storedAccess.pageKeys,
    } : { email: user.mail || user.userPrincipalName, displayName: user.displayName, role: "user", portalAccess: true, pageKeys: [] });
    setError("");
    setMessage("");
  };

  const search = async () => {
    if (query.trim().length < 2) {
      setError("Vul minimaal twee letters of cijfers in om een medewerker te zoeken.");
      return;
    }
    try {
      setError("");
      setSearching(true);
      setMatches(await searchEntraUsers(apiFetch, query));
      setSearched(true);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Medewerkers zoeken is mislukt.");
    } finally {
      setSearching(false);
    }
  };

  const toggle = (key: string) => setForm((current) => ({
    ...current,
    pageKeys: current.pageKeys.includes(key) ? current.pageKeys.filter((value) => value !== key) : [...current.pageKeys, key],
  }));
  const setRole = (role: "user" | "admin") => setForm((current) => ({
    ...current,
    role,
    pageKeys: current.pageKeys.filter((key) => canAssignPortalPage(key, role)),
  }));
  const closeEditor = () => {
    setSelected(null);
    setEditorLocation(null);
  };
  const editExisting = (user: InternalUserAccess) => {
    if (editorLocation === "existing" && selected?.id === user.userObjectId) {
      closeEditor();
      return;
    }
    setSelected({ id: user.userObjectId, displayName: user.displayName, mail: user.email, userPrincipalName: user.email, accountEnabled: user.isActive });
    setEditorLocation("existing");
    setForm({ email: user.email, displayName: user.displayName, role: user.role, portalAccess: user.portalAccess, pageKeys: user.pageKeys });
    setMessage("");
    setError("");
  };

  const save = async () => {
    if (!selected) return;
    try {
      await saveInternalUser(apiFetch, selected.id, form);
      await loadUsers();
      closeEditor();
      setMatches([]);
      setMessage("Toegang opgeslagen.");
      setError("");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Opslaan mislukt.");
    }
  };
  const remove = async () => {
    if (!selected || editorLocation !== "existing") return;
    const name = form.displayName || form.email;
    if (!window.confirm(`Interne toegang voor ${name} (${form.email}) verwijderen?`)) return;
    try {
      setDeleting(true);
      await deleteInternalUser(apiFetch, selected.id);
      await loadUsers();
      closeEditor();
      setMatches([]);
      setMessage("Interne toegang verwijderd.");
      setError("");
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Interne toegang verwijderen is mislukt.");
    } finally {
      setDeleting(false);
    }
  };

  const editor = selected && <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-slate-950/40">
    <h2 className="font-black">{form.displayName}</h2>
    <p className="text-sm text-slate-500">{form.email}</p>
    <div className="mt-5 grid gap-4 md:grid-cols-2">
      <label className="text-sm font-bold">Rol
        <select value={form.role} onChange={(event) => setRole(event.target.value as "user" | "admin")} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-white/15 dark:bg-slate-950">
          <option value="user">User</option><option value="admin">Admin</option>
        </select>
      </label>
      <label className="flex items-center gap-2 pt-6 text-sm font-bold">
        <input type="checkbox" checked={form.portalAccess} onChange={(event) => setForm({ ...form, portalAccess: event.target.checked })} />
        Mag inloggen in de interne portal
      </label>
    </div>
    <div className="mt-5 grid gap-2 md:grid-cols-2">
      {assignablePortalPages.map(({ key, permissionLabel: label }) => <label key={key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-slate-900">
        <input type="checkbox" disabled={!canAssignPortalPage(key, form.role)} checked={form.pageKeys.includes(key)} onChange={() => toggle(key)} />
        {label}
      </label>)}
    </div>
    {form.role === "user" && <p className="mt-2 text-xs text-slate-500">Combo Keys beoordelen is alleen beschikbaar voor admins.</p>}
    <div className="mt-5 flex gap-3">
      <button type="button" onClick={save} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white">Opslaan</button>
      <button type="button" onClick={closeEditor} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold dark:border-white/15">Sluiten</button>
      {editorLocation === "existing" && <button type="button" onClick={remove} disabled={deleting} aria-label="Interne toegang verwijderen" title="Interne toegang verwijderen" className="ml-auto rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-400/30 dark:text-red-300 dark:hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>}
    </div>
  </div>;

  return <section className="it-page max-w-4xl">
    <div><p className="it-eyebrow">Beheercentrum</p><h1 className="it-heading">Gebruikersbeheer</h1><p className="it-description">Alleen superadmins kunnen interne toegang aanpassen.</p></div>
    {message && <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300">{message}</p>}

    <div className="it-surface p-5">
      <label className="text-sm font-bold">Medewerker zoeken</label>
      <div className="mt-2 space-y-3">
        <input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); search(); } }} className="block w-full rounded-lg border border-slate-300 px-3 py-2 dark:border-white/15 dark:bg-slate-950" placeholder="Naam of e-mailadres" />
        <button type="button" disabled={searching} onClick={search} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold text-white disabled:opacity-60">{searching ? "Zoeken..." : "Zoeken"}</button>
      </div>
      {!!matches.length && <div className="mt-2 divide-y rounded-lg border border-slate-200 dark:border-white/10">{matches.map((user) => {
        const email = user.mail || user.userPrincipalName;
        return <button key={user.id} onClick={() => choose(user)} className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-white/5">
          <span className="font-bold">{user.displayName}</span> <span className="text-slate-500">{email}</span>
        </button>;
      })}</div>}
      {searched && !searching && !matches.length && !error && <p className="mt-3 text-sm text-slate-500">Geen medewerkers gevonden.</p>}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {editorLocation === "search" && editor}
    </div>

    <div className="it-surface p-5">
      <h2 className="font-black">Bestaande interne toegang</h2>
      {loadingUsers ? <p className="mt-3 text-sm text-slate-500">Laden...</p> : !existingUsers.length ? <p className="mt-3 text-sm text-slate-500">Nog geen medewerkers met opgeslagen interne toegang.</p> : <div className="mt-3 divide-y rounded-lg border border-slate-200 dark:border-white/10">
        {existingUsers.map((user) => {
          const isOpen = editorLocation === "existing" && selected?.id === user.userObjectId;
          return <div key={user.userObjectId} ref={(element) => { existingUserElements.current[user.userObjectId] = element; }}>
            <button type="button" onClick={() => editExisting(user)} aria-expanded={isOpen} className="block w-full px-3 py-3 text-left hover:bg-slate-50 dark:hover:bg-white/5">
              <div className="flex items-center justify-between gap-3"><span className="font-bold">{user.displayName || user.email}</span><span className="rounded bg-slate-100 px-2 py-1 text-xs font-bold uppercase dark:bg-white/10">{user.role}</span></div>
              <p className="mt-1 text-xs text-slate-500">{user.email} · {user.portalAccess ? pageLabels(user.pageKeys) : "Geen portaltoegang"}</p>
            </button>
            {isOpen && <div className="px-3 pb-3">{editor}</div>}
          </div>;
        })}
      </div>}
    </div>
  </section>;
}
